import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

/**
 * MelanomaCarousel
 * ────────────────────────────────────────────────────────────────
 * A futuristic, stage-style carousel that showcases mobile screenshots
 * of MDCE's Melanoma Scan Beta. Active slide centered & lifted; adjacent
 * slides recede in 3D with reduced opacity, scale, and a subtle blur.
 * Auto-advances every 5s, pauses on hover or while user is dragging.
 * Supports touch swipe, keyboard arrows, prev/next chevrons, and dot nav.
 *
 * All styles are scoped under `.melscan-root` to avoid bleeding into the
 * rest of the site.
 */

const SLIDES = [
  {
    src: "/images/mel-1-home.webp",
    label: "HOME",
    title: "Home Screen",
    caption: "Smart. Simple. Peace of mind.",
  },
  {
    src: "/images/mel-2-guide.webp",
    label: "GUIDE",
    title: "Skin Guide",
    caption: "Learn the ABCDEs of melanoma.",
  },
  {
    src: "/images/mel-3-capture.webp",
    label: "CAPTURE",
    title: "Image Capture",
    caption: "Center the spot. Snap. Analyze.",
  },
  {
    src: "/images/mel-4-next.webp",
    label: "RESULTS",
    title: "Next Steps",
    caption: "Risk score & guided actions.",
  },
  {
    src: "/images/mel-5-scheduler.webp",
    label: "REMIND",
    title: "Schedule a Reminder",
    caption: "Push · Email · SMS rescans.",
  },
  {
    src: "/images/mel-6-history.webp",
    label: "HISTORY",
    title: "Scan History",
    caption: "Every past scan, sortable & secure.",
  },
  {
    src: "/images/mel-7-bodymap.webp",
    label: "MAP",
    title: "Body Map",
    caption: "Track skin health by region.",
  },
  {
    src: "/images/mel-8-profile.webp",
    label: "PROFILE",
    title: "Profile",
    caption: "Account, summary & preferences.",
  },
];

const AUTO_MS = 5200;

export default function MelanomaCarousel({
  slides = SLIDES,
  variant = "phone", // "phone" | "card" — card variant skips the phone notch
  testid = "melanoma-carousel",
  ariaLabel = "MDCE Melanoma Scan Beta screenshots",
}) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [hasEntered, setHasEntered] = useState(false);
  const total = slides.length;
  const touchRef = useRef({ x: 0, dragging: false });
  const rootRef = useRef(null);

  const go = useCallback(
    (dir) => {
      setActive((a) => (a + dir + total) % total);
    },
    [total]
  );
  const jump = (i) => setActive(((i % total) + total) % total);

  // Start the auto-rotation timer only once the carousel has scrolled into
  // view, so a user always sees slide 1 first (not whichever slide the timer
  // happened to be on while the carousel was still off-screen).
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setHasEntered(true);
            io.disconnect();
            break;
          }
        }
      },
      { threshold: 0.35 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-advance — only runs after the carousel has entered the viewport.
  useEffect(() => {
    if (!hasEntered || paused) return undefined;
    const id = setInterval(() => setActive((a) => (a + 1) % total), AUTO_MS);
    return () => clearInterval(id);
  }, [hasEntered, paused, total]);

  // Keyboard support when carousel is focused
  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const onKey = (e) => {
      if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [go]);

  // Touch handlers
  const onTouchStart = (e) => {
    touchRef.current = { x: e.touches[0].clientX, dragging: true };
    setPaused(true);
  };
  const onTouchEnd = (e) => {
    const start = touchRef.current.x;
    const end = e.changedTouches[0].clientX;
    const dx = end - start;
    if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
    touchRef.current.dragging = false;
    setPaused(false);
  };

  // Compute per-slide style based on signed offset from `active`
  const slideStyle = (i) => {
    let off = i - active;
    // Wrap to shortest path
    if (off > total / 2) off -= total;
    if (off < -total / 2) off += total;

    const abs = Math.abs(off);
    const visible = abs <= 2;

    // X translate scales with offset; depth (scale, opacity, blur) shrinks
    const translateX = off * 58; // % of phone width
    const scale = abs === 0 ? 1 : abs === 1 ? 0.78 : 0.58;
    const opacity = abs === 0 ? 1 : abs === 1 ? 0.55 : abs === 2 ? 0.2 : 0;
    const blur = abs === 0 ? 0 : abs === 1 ? 1.5 : 3;
    const rotateY = off * -18; // subtle 3D rotation toward viewer
    const z = 50 - abs;

    return {
      transform: `translate(-50%, -50%) translateX(${translateX}%) scale(${scale}) rotateY(${rotateY}deg)`,
      opacity: visible ? opacity : 0,
      filter: `blur(${blur}px)`,
      zIndex: z,
      pointerEvents: abs === 0 ? "auto" : "none",
    };
  };

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      className={`melscan-root relative w-full select-none outline-none ${variant === "card" ? "is-card" : ""}`}
      data-testid={testid}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      aria-roledescription="carousel"
      aria-label={ariaLabel}
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[70%] h-[80%] bg-[radial-gradient(ellipse_at_center,rgba(138,61,255,0.28),transparent_70%)] blur-3xl" />
      </div>

      {/* HUD readout */}
      <div className="relative z-10 flex items-center justify-between mb-6 px-2 sm:px-4">
        <div className="flex items-center gap-2 font-mono-tech text-[11px] tracking-[0.22em] text-[#d6b8ff]">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#a06bff] animate-pulse" />
          FRAME · 0{active + 1} / 0{total}
        </div>
        <div className="hidden sm:flex items-center gap-2 font-mono-tech text-[11px] tracking-[0.22em] text-[#a991d6]">
          {slides[active].label}
          <span className="text-[#5a3a93]">·</span>
          <span className="text-white/70">{slides[active].title}</span>
        </div>
      </div>

      {/* Stage */}
      <div className="melscan-stage relative h-[560px] sm:h-[620px] lg:h-[660px] overflow-hidden">
        {/* Floor reflection grid */}
        <div className="melscan-floor pointer-events-none" />

        {slides.map((s, i) => (
          <button
            key={s.src}
            type="button"
            onClick={() => jump(i)}
            className="melscan-slide"
            style={slideStyle(i)}
            aria-label={`Show ${s.title}`}
            aria-current={i === active}
            data-testid={`${testid}-slide-${i}`}
            tabIndex={i === active ? 0 : -1}
          >
            <div className="melscan-phone">
              {/* Corner brackets */}
              <span className="melscan-c melscan-c-tl" />
              <span className="melscan-c melscan-c-tr" />
              <span className="melscan-c melscan-c-bl" />
              <span className="melscan-c melscan-c-br" />
              {/* Top notch — only for phone variant */}
              {variant === "phone" && <span className="melscan-notch" />}
              {/* Screenshot */}
              <img
                src={s.src}
                alt={s.title}
                loading="lazy"
                draggable="false"
                className="melscan-img"
              />
              {/* Scanning sweep — only on the active card */}
              {i === active && <span className="melscan-sweep" />}
            </div>
          </button>
        ))}

        {/* Prev / Next */}
        <button
          type="button"
          aria-label="Previous slide"
          data-testid={`${testid}-prev`}
          onClick={() => go(-1)}
          className="melscan-nav melscan-nav-l"
        >
          <ChevronLeft size={22} />
        </button>
        <button
          type="button"
          aria-label="Next slide"
          data-testid={`${testid}-next`}
          onClick={() => go(1)}
          className="melscan-nav melscan-nav-r"
        >
          <ChevronRight size={22} />
        </button>
      </div>

      {/* Caption + dots */}
      <div className="relative z-10 mt-6 flex flex-col items-center gap-4">
        <div
          key={active /* re-trigger fade on each change */}
          className="melscan-caption text-center max-w-md px-4"
          data-testid={`${testid}-caption`}
        >
          <div className="font-display text-white text-lg lg:text-xl">
            {slides[active].title}
          </div>
          <div className="text-[#cfc4e6] text-sm mt-1">
            {slides[active].caption}
          </div>
        </div>

        <div className="flex items-center gap-2.5" role="tablist" aria-label="Carousel dots">
          {slides.map((_, i) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`${testid}-dot-${i}`}
              onClick={() => jump(i)}
              className={`melscan-dot ${i === active ? "is-active" : ""}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
